import React from 'react';
import { useFieldArray, Control, UseFormRegister, FieldErrors } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, TrashIcon, MixIcon } from '@radix-ui/react-icons';

import { Box, Button, Stack, IconButton } from '../../index';

interface DynamicFormArrayProps {
  control: Control<Record<string, unknown>>;
  register: UseFormRegister<Record<string, unknown>>;
  name: string;
  errors?: FieldErrors;
  renderField: (field: unknown, index: number, remove: (index: number) => void) => React.ReactNode;
  defaultValue?: unknown;
  minItems?: number;
  maxItems?: number;
  addButtonText?: string;
  emptyMessage?: string;
  onAdd?: () => void;
  onRemove?: (index: number) => void;
  sortable?: boolean;
}

export function DynamicFormArray({
  control,
  // register,
  name,
  // errors,
  renderField,
  defaultValue = {},
  minItems = 0,
  maxItems,
  addButtonText = 'Yeni Ekle',
  emptyMessage = 'Henüz kayıt yok',
  onAdd,
  onRemove,
  sortable = false,
}: DynamicFormArrayProps) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: name as never,
  });

  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);

  const handleAdd = () => {
    if (!maxItems || fields.length < maxItems) {
      append(defaultValue);
      onAdd?.();
    }
  };

  const handleRemove = (index: number) => {
    if (fields.length > minItems) {
      remove(index);
      onRemove?.(index);
    }
  };

  const handleDragStart = (e: unknown, index: number) => {
    setDraggedIndex(index);
    const dragEvent = e as React.DragEvent;
    if (dragEvent.dataTransfer) {
      dragEvent.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragOver = (e: unknown) => {
    const dragEvent = e as React.DragEvent;
    dragEvent.preventDefault();
    if (dragEvent.dataTransfer) {
      dragEvent.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDrop = (e: unknown, dropIndex: number) => {
    const dragEvent = e as React.DragEvent;
    dragEvent.preventDefault();

    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      move(draggedIndex, dropIndex);
    }

    setDraggedIndex(null);
  };

  return (
    <Box>
      <AnimatePresence mode="popLayout">
        {fields.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <Box
              padding="24px"
              textAlign="center"
              border="1px solid"
              borderStyle="dashed"
              borderColor="#E5E7EB"
              borderRadius="8px"
              color="#6B7280"
            >
              {emptyMessage}
            </Box>
          </motion.div>
        ) : (
          <Stack gap="12px">
            {fields.map((field, index) => (
              <motion.div
                key={field.id}
                layout
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.2 }}
                style={{
                  opacity: draggedIndex === index ? 0.5 : 1,
                }}
              >
                <div
                  {...(sortable
                    ? {
                        draggable: true,
                        onDragStart: (e: React.DragEvent) => handleDragStart(e, index),
                        onDragOver: handleDragOver,
                        onDrop: (e: React.DragEvent) => handleDrop(e, index),
                      }
                    : {})}
                  style={{
                    cursor: sortable ? 'move' : 'default',
                  }}
                >
                  <Box
                    position="relative"
                    border="1px solid"
                    borderColor="#E5E7EB"
                    borderRadius="8px"
                    padding="16px"
                    backgroundColor="#FFFFFF"
                    transition="all 0.2s"
                    _hover={{
                      borderColor: '#D1D5DB',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {sortable && (
                      <Box
                        position="absolute"
                        left="4px"
                        top="50%"
                        transform="translateY(-50%)"
                        color="#9CA3AF"
                        cursor="grab"
                        _active={{ cursor: 'grabbing' }}
                      >
                        <MixIcon />
                      </Box>
                    )}

                    <Box paddingLeft={sortable ? '24px' : '0'}>
                      {renderField(field, index, () => handleRemove(index))}
                    </Box>

                    {fields.length > minItems && (
                      <Box position="absolute" right="8px" top="8px">
                        <IconButton
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => handleRemove(index)}
                          aria-label="Sil"
                          icon={<TrashIcon />}
                        />
                      </Box>
                    )}
                  </Box>
                </div>
              </motion.div>
            ))}
          </Stack>
        )}
      </AnimatePresence>

      <Box marginTop="16px">
        <Button
          variant="outline"
          onClick={handleAdd}
          disabled={maxItems ? fields.length >= maxItems : false}
          leftIcon={<PlusIcon />}
        >
          {addButtonText}
        </Button>
      </Box>

      {maxItems && fields.length >= maxItems && (
        <Box marginTop="8px" fontSize="14px" color="#F59E0B">
          Maksimum {maxItems} kayıt ekleyebilirsiniz
        </Box>
      )}
    </Box>
  );
}
